// Role → permission gating for nav, routes and actions. The role/permission
// matrix lives in @safiri/shared and is the SAME one the backend enforces
// (apps/backend/src/config/adminRoutePermissions.ts), so hiding something here
// matches what the API would refuse.
import { roleHasPermission, type AdminPermission } from "@safiri/shared";
import type { AdminRole } from "../types/admin";

export type { AdminPermission };

// Stable module keys used by the nav config + RoleGate.
export type ModuleKey =
  | "home"
  | "live"
  | "inbox"
  | "finance"
  | "analytics"
  | "monthly"
  | "dashboard"
  | "drivers"
  | "referrals"
  | "trips"
  | "users"
  | "ride-types"
  | "toll-locations"
  | "tags"
  | "coupons"
  | "transactions"
  | "settlements"
  | "payouts"
  | "payout-catalogue"
  | "tax-receipts"
  | "latra"
  | "settings"
  | "reports"
  | "sos"
  | "queries"
  | "lost-items"
  | "admins"
  | "audit"
  | "account";

// The permission a module's screen needs. null = every signed-in admin.
const MODULE_PERMISSION: Record<ModuleKey, AdminPermission | null> = {
  home: null,
  live: "live.view",
  inbox: "dashboard.view",
  finance: "finance.view",
  analytics: "analytics.view",
  monthly: "reports.view",
  dashboard: "dashboard.view",
  drivers: "drivers.view",
  referrals: "referrals.view",
  trips: "trips.view",
  users: "riders.view",
  "ride-types": "pricing.view",
  "toll-locations": "pricing.view",
  tags: "tags.manage",
  coupons: "promos.view",
  transactions: "finance.view",
  settlements: "finance.manage",
  payouts: "payouts.view",
  "payout-catalogue": "payouts.view",
  "tax-receipts": "compliance.view",
  latra: "compliance.view",
  settings: "settings.manage",
  reports: "reports.view",
  sos: "sos.view",
  queries: "support.view",
  "lost-items": "support.view",
  admins: "admins.manage",
  audit: "audit.view",
  account: null,
};

export function can(
  role: AdminRole | undefined,
  permission: AdminPermission,
): boolean {
  // Role not loaded yet → don't hide anything (the API still decides).
  if (!role) return true;
  return roleHasPermission(role, permission);
}

export function canAccess(
  role: AdminRole | undefined,
  module: ModuleKey,
): boolean {
  const permission = MODULE_PERMISSION[module];
  return permission === null || can(role, permission);
}
